import { StorageKey } from 'enums/StorageKey';
import { ThemeModeType } from 'enums/ThemeModeType';

export const supportedThemeModes = {
    LIGHT: true,
    DARK: true,
};

export const supportedColorSchemes = {
    SKY: true,
    OCEAN: true,
    FLOWER: true,
};

export const colorSchemeNames: Record<keyof typeof supportedColorSchemes, string> = {
    SKY: 'Небо',
    OCEAN: 'Океан',
    FLOWER: 'Цветок',
};

export const defaultColorSchemeName = 'Классическая';

export type ThemeMode = ThemeModeType;
export type ColorScheme = keyof typeof supportedColorSchemes;

const getDefaultPreferredTheme = (): ThemeMode => {
    let themeMode: ThemeMode = ThemeModeType.LIGHT;
    try {
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            themeMode = ThemeModeType.DARK;
        }
    } catch {}
    return themeMode;
};

export const setWidgetRootTheme = (root: HTMLElement, theme: string) => {
    root.setAttribute('data-widget-theme', theme);
};

let overrideThemeMode: ThemeMode;

export const getCurrentThemeMode = () => {
    if (overrideThemeMode) return overrideThemeMode;

    const storedThemeMode = localStorage.getItem(StorageKey.ThemeMode) as ThemeMode;

    const themeMode: ThemeMode =
        storedThemeMode && supportedThemeModes[storedThemeMode]
            ? storedThemeMode
            : getDefaultPreferredTheme();

    return themeMode;
};

export const setCurrentThemeMode = (themeMode: ThemeMode) => {
    if (themeMode == null) {
        localStorage.removeItem(StorageKey.ThemeMode);

        return;
    }

    localStorage.setItem(StorageKey.ThemeMode, themeMode);
};

export const getCurrentColorScheme = () => {
    const storedScheme = localStorage.getItem(StorageKey.ColorScheme) as ColorScheme;

    const scheme = storedScheme && supportedColorSchemes[storedScheme] ? storedScheme : null;

    return scheme;
};

export const setCurrentColorScheme = (scheme: ColorScheme) => {
    if (scheme == null) {
        localStorage.removeItem(StorageKey.ColorScheme);

        return;
    }

    localStorage.setItem(StorageKey.ColorScheme, scheme);
};

export const getCurrentTheme = (themeMode?: ThemeMode, colorScheme?: ColorScheme) => {
    themeMode = themeMode === undefined ? getCurrentThemeMode() : themeMode ?? ThemeModeType.LIGHT;
    colorScheme = colorScheme === undefined ? getCurrentColorScheme() : colorScheme;

    return colorScheme ? colorScheme + '_' + themeMode : themeMode;
};

export const watchExternalThemeChange = (
    getThemedElement: () => HTMLElement,
    overrideTheme?: ThemeMode,
) => {
    overrideThemeMode = overrideTheme;

    let intervalHandle: ReturnType<typeof setInterval> = null;

    intervalHandle = setInterval(() => {
        setWidgetRootTheme(getThemedElement(), getCurrentTheme());
    }, 50);

    return () => {
        overrideThemeMode = undefined;
        clearInterval(intervalHandle);
        getThemedElement()?.removeAttribute('data-widget-theme');
    };
};
